import Modal from '../Modal';
import { useEffect, useMemo, useState } from 'react';
import { useModal } from '../../../providers/ModalProvider';
import { modals } from '../../../providers/modals';
import UserMultiSelector from '../../UserMultiSelector';
import { Text } from '../../typography';
import { useTranslation } from 'react-i18next';
import { keys } from '../../../utilities/translator/translation_keys';
import { Form } from 'react-bootstrap';
import styles from './RecordMeetingModal.module.scss';
import { useWorkspace } from '../../../providers/WorkspaceProvider';
import { loader } from 'graphql.macro';
import { useMutation } from '@apollo/client';
import { Icon } from '@mui/material';
import { showToast } from '../../../graphql/cache/modal';
import Button from '../../Button';
import { Spinner } from 'react-bootstrap';
import findFileIcon, { imageExtensions } from '../../../utilities/files';
import { fileType, toastLength, toastVariant } from '../../../constants/misc';
import { useCurrentUser } from '../../../providers/UserProvider';
import { customColors } from '../../../utilities';
import InspectionSignatures from '../../../pages/inspection_page/InspectionSignatures';
import SignatureCanvas from '../../SignatureCanvas';
import { useOnlineStatus } from '../../../hooks/offline-hooks/offline-misc';
import VoiceRecorder from '../../VoiceRecorder';
import QuickFileDropzone from '../../image_uploads/QuickFileDropzone';
import { useAIMutations } from '../../../hooks/AI/AI_hooks';
import { useVoiceRecorder } from '../../../providers/VoiceRecorderProvider';
import TextWithIcon from '../../typography/TextWithIcon';
import { useInspectionMutations } from '../../../hooks/inspection/inspection_auxilary';
import Dropdown from '../../dropdownV2/Dropdown';
import { useChangeWorkspace } from '../../../hooks/misc';
import SimpleWorkspaceCard from '../../SimpleWorkspaceCard';

const addInspectionAttachmentMutation = loader(
  '../../../pages/inspection_page/Inspection.addAttachment.graphql',
);

export default function RecordMeetingModal() {
  const { closeModal, modalState, updateModal, openConfirmationModal } =
    useModal();
  const { user: currentUser } = useCurrentUser();
  const { workspaceId, workspace, availableWorkspaces } = useWorkspace();
  const { t } = useTranslation();
  const [addInspectionAttachment] = useMutation(
    addInspectionAttachmentMutation,
  );

  const [loadingAttachment, setLoadingAttachment] = useState();
  const [notes, setNotes] = useState();
  const [showSignatures, setShowSignatures] = useState(false);
  const [allWorkspaces, setAllWorkspaces] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [hasRecording, setHasRecording] = useState(false);
  const [manual, setManual] = useState(false);
  const workspaceOptions = useMemo(
    () => availableWorkspaces || [],
    [availableWorkspaces],
  );
  const { changeWorkspace } = useChangeWorkspace();
  const [selectedWorkspace, setSelectedWorkspace] = useState(workspace);

  const {
    updateInspection,
    addRegret,
    addParticipant,
    addAttachment,
    deleteInspection,
    updateInspectionItem,
  } = useInspectionMutations();

  const online = useOnlineStatus();
  const { summarizeAudio } = useAIMutations();
  const {
    audioURL,
    stopRecording,
    setAudioURL,
    audioChunksRef,
    ready,
    setReady,
    isRecording,
  } = useVoiceRecorder();

  const {
    show,
    participants,
    location,
    description,
    safetyTopic,
    attachments,
    refetch,
    onView,
    regrets,
    signatures = [],
    title,
    inspection,
  } = modalState.recordMeetingModal;

  const update = (variables) => {
    updateModal({
      modalName: modals.recordMeeting,
      variables: { ...variables },
    });
  };

  const updateQuestion = ({ target, value }) => {
    const { template } = inspection || {};
    if (!template) return;
    const questions = template.categories[0]?.questions || [];
    const targetQuestionId = questions.find((q) => q.title === target)?.id;
    const inspectionItemId = inspection?.items?.find(
      (i) => i.questionId === targetQuestionId,
    )?.id;
    if (!inspectionItemId || !targetQuestionId) return;

    updateInspectionItem({ id: inspectionItemId, value });
  };

  const onSubmit = async () => {
    await summarizeAudio({
      inspectionId: inspection.id,
    });
    try {
      if (signatures?.length) {
        for (let signature of signatures) {
          await addInspectionAttachment({
            variables: {
              url: signature.url,
              inspectionId: inspection.id,
              name: signature.name,
              extension: 'SIGNATURE',
            },
          });
        }
      }
      if (attachments?.length) {
        for (let file of attachments) {
          await addInspectionAttachment({
            variables: {
              url: file.url,
              inspectionId: inspection.id,
              name: file.name,
              extension: file.extension,
            },
          });
        }
      }
      await updateInspection({
        ...modalState.recordMeetingModal,
        id: inspection.id,
        notes,
        workspaceId: allWorkspaces === true ? null : workspaceId,
      });
      if (participants?.length) {
        for (let user of participants) {
          await addParticipant({
            userId: user.id,
            inspectionId: inspection.id,
          });
        }
      }
      if (regrets?.length) {
        for (let user of regrets) {
          await addRegret({
            userId: user.id,
            inspectionId: inspection.id,
          });
        }
      }
      await updateInspection({
        id: inspection.id,
        isDraft: false,
      });
    } catch (err) {
      console.error(err);
    } finally {
      if (!!audioChunksRef?.current) {
        audioChunksRef.current = [];
      }
      onClose();
    }
  };
  const onClose = () => {
    setNotes('');
    stopRecording();
    setUpdating(false);
    setShowSignatures(false);
    setAudioURL(null);
    setAllWorkspaces(false);
    refetch();
    setHasRecording(false);
    closeModal({ modalName: modals.recordMeeting });
  };
  const onHide = () => {
    openConfirmationModal({
      title: t(keys.assessments.CLOSE_CONFIRMATION_TITLE),
      description: t(keys.assessments.CLOSE_CONFIRMATION_MESSAGE),
      variant: 'danger',
      buttonText: t(keys.action_items.CLOSE),
      onSubmit: async () => {
        if (!!inspection?.id) {
          await deleteInspection({ id: inspection.id });
        }
        onClose();
      },
    });
  };
  useEffect(() => {
    if (!!audioURL?.url && !!ready && !updating && show && !!inspection?.id) {
      setUpdating(true);
      const handleAudioAttachment = async () => {
        await addAttachment({
          inspectionId: inspection.id,
          file: { ...audioURL, fileType: 'AUDIO' },
        });

        setReady(false);
        setUpdating(false);
      };
      handleAudioAttachment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioURL, ready, inspection, show]);

  function handleWorkspaceChange(w) {
    const id = w?.id ?? null;
    const isAll = id == null;
    setSelectedWorkspace(w);
    setAllWorkspaces(isAll);
    if (!isAll) changeWorkspace(id);
    return;
  }
  const userListType = useMemo(() => {
    if (!!selectedWorkspace && !allWorkspaces) {
      return 'workspace';
    } else return 'all';
  }, [allWorkspaces, selectedWorkspace]);

  const footer = (
    <div className={styles.footer}>
      <div className={styles.check}>
        <div className={styles.checkContainer}>
          <Text noMargin weight="semiBold">
            {t(keys.common.SHARED)}
          </Text>
          <Form.Check
            type="switch"
            onChange={(e) => {
              const nextAll = e.target.checked;
              setAllWorkspaces(nextAll);
            }}
            checked={allWorkspaces === true}
          />
        </div>
      </div>
      {ready ? (
        <div>
          <Spinner
            className={styles.spinner}
            animation="border"
            variant="primary"
          />
        </div>
      ) : (
        <Button
          disabled={isRecording || showSignatures || ready}
          value={t(keys.action.SUBMIT)}
          onClick={() => {
            openConfirmationModal({
              title: t(keys.assessments.SUBMIT_CONFIRMATION_TITLE),
              description: t(keys.assessments.SUBMIT_CONFIRMATION_MESSAGE),
              variant: 'warning',
              onSubmit: async () => {
                try {
                  await onSubmit();
                } catch (error) {
                  console.error(error);
                } finally {
                  onClose();
                  showToast({
                    title: t(keys.assessments.SUBMIT_TITLE),
                    message: t(keys.assessments.SUBMIT_TITLE),
                    variant: toastVariant.info,
                    time: toastLength.md,
                  });
                }
              },
            });
          }}
        />
      )}
    </div>
  );

  return (
    <Modal
      open={show || false}
      onClose={onHide}
      title={t(keys.assessments.RECORD_MEETING)}
      allowClose={!isRecording}
      hideCancel
      hideSubmit
      beta
      submitDisabled={!inspection?.id}
      footer={footer}
      className={styles.fullScreen}
      titleImage={
        <Text size="lg" noMargin>
          📆
        </Text>
      }
    >
      {!inspection?.id ? (
        <div className={styles.emptyState}>
          <br />
          <br />
          <br />
          <Icon sx={{ fontSize: '3rem', color: customColors.ORANGE }}>
            warning
          </Icon>
          <Text noMargin textAlign="center" weight="semiBold">
            {t(keys.assessments.NO_MEETING_DATA)}
          </Text>
          <Text size="sm" textAlign="center">
            {t(keys.assessments.ISSUE_PERSIST)}
          </Text>
        </div>
      ) : (
        <div className={styles.container}>
          <div className={styles.leftContainer}>
            <div className={styles.recorder}>
              <VoiceRecorder
                onStop={async () => {
                  setHasRecording(true);
                  await stopRecording();
                }}
                audioFile={inspection?.audioURL || audioURL}
                hasRecording={hasRecording}
              />
              <div className={styles.fullWidth}>
                {!manual ? (
                  <Text
                    textAlign="center"
                    color="accentPrimary"
                    hover
                    onClick={() => setManual(true)}
                    noMargin
                    className={styles.fullWidth}
                  >
                    {t(keys.assessments.ENTER_MANUALLY)}
                  </Text>
                ) : (
                  <Form.Control
                    as="textarea"
                    rows={5}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    onBlur={(e) =>
                      updateInspection({
                        id: inspection.id,
                        notes: e.target.value,
                      })
                    }
                  />
                )}
              </div>
            </div>

            <div className={styles.spacer} />
            <Text noMargin weight="semiBold">
              Meeting Title
            </Text>
            <Form.Control
              onChange={(e) => update({ title: e.target.value })}
              value={title}
              onBlur={() => {
                updateInspection({ id: inspection.id, subject: title });
              }}
            />
            <div className={styles.header}>
              <Text noMargin weight="semiBold">
                {t(keys.assessments.SAFETY_TOPIC)}
              </Text>
              {!!onView && (
                <Text
                  hover
                  onClick={() => onView()}
                  noMargin
                  size="sm"
                  color="accentPrimary"
                >
                  {t(keys.assessments.VIEW)}
                </Text>
              )}
            </div>
            <Form.Control
              value={safetyTopic}
              as="textarea"
              rows={3}
              onChange={(e) => update({ safetyTopic: e.target.value })}
              onBlur={() =>
                updateQuestion({
                  target: 'Safety Topic',
                  value: safetyTopic,
                })
              }
              className={styles.input}
            />
            <Text noMargin weight="semiBold">
              {t(keys.assessments.LOCATION)}
            </Text>
            <Form.Control
              as="textarea"
              rows={1}
              value={location}
              onChange={(e) => update({ location: e.target.value })}
              className={styles.input}
              onBlur={(e) =>
                updateQuestion({
                  target: 'Location',
                  value: e.target.value,
                })
              }
            />
            <Text noMargin weight="semiBold">
              {t(keys.assessments.MEETING_DESCRIPTION)}
            </Text>
            <Form.Control
              as="textarea"
              value={description}
              rows={4}
              onChange={(e) => update({ description: e.target.value })}
              className={styles.input}
              onBlur={(e) =>
                updateQuestion({
                  target: 'Meeting Description',
                  value: e.target.value,
                })
              }
            />
          </div>
          <div className={styles.rightContainer}>
            <Text noMargin weight="semiBold">
              {t(keys.common.WORKSPACE)}
            </Text>
            {isRecording ? (
              <div className={styles.workspaceHolder}>
                <SimpleWorkspaceCard workspace={workspace} weight="regular" />
              </div>
            ) : (
              <Dropdown
                readOnly={isRecording}
                selected={allWorkspaces ? null : workspace}
                options={workspaceOptions}
                onChange={handleWorkspaceChange}
                type="workspace"
              />
            )}
            <Text noMargin weight="semiBold">
              {t(keys.common.PARTICIPANTS)}
            </Text>
            <UserMultiSelector
              stateUpdateOnly
              setUsers={(participants) => update({ participants })}
              type={userListType}
              otherWorkspace={!allWorkspaces ? selectedWorkspace : null}
              selected={participants}
              onUserAdded={(user) =>
                update({ participants: [...participants, user] })
              }
              onUserRemoved={(user) => {
                update({
                  participants: participants.filter((u) => u.id !== user.id),
                });
              }}
            />
            <Text noMargin weight="semiBold">
              {t(keys.common.REGRETS)}
            </Text>
            <UserMultiSelector
              disableSelectAll
              type={userListType}
              otherWorkspace={!allWorkspaces ? selectedWorkspace : null}
              selected={regrets}
              onUserAdded={(user) => update({ regrets: [...regrets, user] })}
              onUserRemoved={(user) => {
                update({
                  regrets: regrets.filter((u) => u.id !== user.id),
                });
              }}
            />
            {loadingAttachment ? (
              <Spinner
                className={styles.spinner}
                animation="border"
                variant="primary"
              />
            ) : (
              <>
                <QuickFileDropzone
                  header={t(keys.common.ATTACHMENTS)}
                  icon={!!attachments?.length}
                  iconText={null}
                  setLoading={setLoadingAttachment}
                  iconSize={'1.5rem'}
                  onSubmit={({ url, name }) => {
                    setLoadingAttachment(true);
                    const extension = url.split('.').pop();
                    update({
                      attachments: [
                        ...(attachments || []),
                        {
                          url,
                          name,
                          downloadAllowed: true,
                          fileType: fileType[extension],
                          creator: currentUser,
                          extension,
                        },
                      ],
                    });
                    setLoadingAttachment(false);
                  }}
                />
                {!!attachments?.length &&
                  attachments.map((a) => {
                    const isImage = imageExtensions.includes(a.extension);
                    const src = isImage ? a.url : findFileIcon(a.extension);
                    return (
                      <div className={styles.flexRow} key={a.imageUrl || a.url}>
                        <img
                          className={styles.fileImage}
                          src={src}
                          alt="file"
                        />
                        <Text noMargin>{a.name}</Text>
                        <Icon
                          baseClassName="material-icons-outlined"
                          style={{ color: 'red', fontSize: '1.2rem' }}
                          onClick={() =>
                            update({
                              attachments: attachments.filter(
                                (att) => att.url !== a.url,
                              ),
                            })
                          }
                        >
                          delete
                        </Icon>
                      </div>
                    );
                  })}
              </>
            )}
            <div className={styles.header}>
              <TextWithIcon
                icon="draw"
                noMargin
                weight="semiBold"
                iconProps={{ fontSize: '1.2rem' }}
              >
                {!!showSignatures ? 'Add Signature' : 'Signatures'}
              </TextWithIcon>
              <Icon
                onClick={() => setShowSignatures(!showSignatures)}
                sx={{ color: customColors.BLUE, cursor: 'pointer' }}
              >
                {showSignatures ? 'cancel' : 'add'}
              </Icon>
            </div>
            {online &&
              (!showSignatures ? (
                <div className={styles.signatures}>
                  <InspectionSignatures
                    size="sm"
                    attachments={signatures || {}}
                  />
                </div>
              ) : (
                <div className={styles.signatureContainer}>
                  <SignatureCanvas
                    onSubmit={(url, failed, pass, name) => {
                      const dateCreated = new Date().getTime();
                      const signature = {
                        url,
                        name,
                        dateCreated,
                        creator: currentUser,
                      };
                      update({ signatures: [...signatures, signature] });
                      setShowSignatures(false);
                    }}
                  />
                </div>
              ))}
          </div>
        </div>
      )}
    </Modal>
  );
}
